import React from 'react';
import FullBlockingModal from '../common/FullBlockingModal';
import { Button } from '../common/Button';
import { QuoteJob } from '../../types/quote';
import { useTranslation } from 'react-i18next';
import { formatDateLocalized } from '../../utils/dateUtils';
import { getFreelancerSubtitle } from '../../utils/freelancerUtils';
import FormattedCurrency from '../common/FormattedCurrency';

interface ProjectJobDetailsModalProps {
  job: QuoteJob;
  onClose: () => void;
}

const ProjectJobDetailsModal: React.FC<ProjectJobDetailsModalProps> = ({ job, onClose }) => {
  const { t, i18n } = useTranslation(['projects', 'common']);

  return (
    <FullBlockingModal innerClassName="md:max-w-[72rem]">
      <button
        className="absolute -top-1 right-1 text-gray-400 hover:text-gray-600 text-2xl md:top-0 md:right-4"
        onClick={onClose}
        aria-label={t('common:form_actions.close')}
      >
        &times;
      </button>

      <h2 className="text-2xl font-bold text-secondary mb-2">
        {t('viewJobOffer', { ns: 'projects', defaultValue: "Visualiser une offre d'emploi" })}
      </h2>

      <div className="text-xl font-bold text-secondary mb-6">{job.title}</div>

      {/* Original Job Offer Section */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-secondary mb-4">
          {t('originalJobOffer', { ns: 'projects', defaultValue: "Offre d'emploi original" })}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column - Project Details */}
          <div className="space-y-4">
            <div>
              <span className="text-sm text-secondary">
                {t('serviceType', { ns: 'projects', defaultValue: 'Types de services requis' })}:
              </span>
              <div className="font-bold text-secondary">
                {getFreelancerSubtitle(
                  { service_category_slugs: job.required_services.map(s => s.slug) },
                  t
                )}
              </div>
            </div>

            <div>
              <span className="text-sm text-secondary">
                {t('location', { ns: 'projects', defaultValue: 'Localisation' })}:
              </span>
              <div className="font-bold text-secondary">{job.city.name}</div>
            </div>

            <div>
              <span className="text-sm text-secondary">
                {t('onSite', { ns: 'projects', defaultValue: 'Travail en présentiel' })}:
              </span>
              <div className="font-bold text-secondary">
                {job.on_site
                  ? t('form_fields.yes', { ns: 'common' })
                  : t('form_fields.no', { ns: 'common' })}
              </div>
            </div>

            <div>
              <span className="text-sm text-secondary">
                {t('languages', { ns: 'projects', defaultValue: 'Langue(s) parlée(s)' })}:
              </span>
              <div className="font-bold text-secondary">
                {job.languages?.map(l => t(`language.${l}`, { ns: 'common' })).join(', ')}
              </div>
            </div>
          </div>

          {/* Right Column - Financial & Dates */}
          <div className="space-y-4">
            <div>
              <span className="text-sm text-secondary">
                {t('budget', { ns: 'projects', defaultValue: 'Budget' })}:
              </span>
              <div className="font-bold text-secondary">
                <FormattedCurrency value={job.budget_range.min_amount || 0} /> à{' '}
                <FormattedCurrency value={job.budget_range.max_amount || 0} />
              </div>
            </div>

            <div>
              <span className="text-sm text-secondary">
                {t('requestedStartDate', {
                  ns: 'projects',
                  defaultValue: 'Date de commencement demandée',
                })}
                :
              </span>
              <div className="font-bold text-secondary">
                {job.start_date &&
                  formatDateLocalized(job.start_date, i18n.language, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
              </div>
            </div>

            <div>
              <span className="text-sm text-secondary">
                {t('requestedProjectDeadline', {
                  ns: 'projects',
                  defaultValue: "Date d'échéance du projet demandée",
                })}
                :
              </span>
              <div className="font-bold text-secondary">
                {job.end_date &&
                  formatDateLocalized(job.end_date, i18n.language, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <span className="text-sm text-secondary">
              {t('projectDescription', { ns: 'projects', defaultValue: 'Descriptif du projet' })}
            </span>
            <p className="font-bold text-secondary">{job.description}</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end">
        <Button
          className="font-bold rounded-lg px-8 py-3"
          variant="primary"
          onClick={onClose}
          type="button"
        >
          {t('close', { ns: 'projects', defaultValue: 'Fermer' })}
        </Button>
      </div>
    </FullBlockingModal>
  );
};

export default ProjectJobDetailsModal;
